import * as React from 'react';
import { useEffect, useState } from 'react';
import Confetti from 'react-dom-confetti';
import { useSelector } from 'react-redux';

import { Box, Flex, H2, Text } from '@opendoor/bricks/core';
import ImgixImg from '@opendoor/bricks/utils/ImgixImg';
import { EventTrackingFn } from '@opendoor/shared-fe/build/declarations/EventTracking';

import CircularImage from 'src/components/common/CircularImage';
import { ReceptionLoader } from 'src/components/ReceptionLoader';
import { ProductOffering } from 'src/declarations';
import { CXA_EMAIL_NEW } from 'src/helpers/globals';

import FAQSection from '../../components/sellerFlow/FAQSection';
import layoutWrapper from '../../hocs/layoutWrapper';
import {
  lastListingPhotos as getLastListingPhotos,
  offerPresent as getOfferPresent,
} from '../../store/sellerFlow/selectors';

const DEFAULT_HOME_PHOTO = 'https://imgdrop.imgix.net/whats_next_home.svg';

interface IProps {
  answers: Map<string, any>;
  lastListingPhotos: string[];
  leadUUIDToken: string;
  offerPresent: boolean;
  submittedByAgent: boolean;
  channel: string;
  trackEvent: EventTrackingFn;
  productOfferings: Array<ProductOffering>;
}

const FlowComplete: React.FC<IProps> = () => {
  const lastListingPhotos = useSelector(getLastListingPhotos);
  const offerPresent = useSelector(getOfferPresent);

  const [blowConfetti, setBlowConfetti] = useState(false);
  const lastListingPhoto = lastListingPhotos && lastListingPhotos[0];

  useEffect(() => {
    if (!blowConfetti) {
      setTimeout(() => {
        setBlowConfetti(true);
      }, 800);
    }
  }, [blowConfetti]);

  const renderHeadline: () => React.ReactNode = () =>
    'We’re hard at work preparing your estimated offer!';

  const renderSubText = () => {
    return (
      // eslint-disable-next-line react/jsx-pascal-case
      <Text as="div" fontSize="s1" lineHeight="s0" mt={9}>
        Most customers receive their offer in less than 24 hours.
      </Text>
    );
  };

  return (
    <ReceptionLoader>
      {offerPresent ? (
        <Box as="section" px={5} maxWidth="1200px" mx="auto">
          <Box textAlign="center">
            <H2 mb={3}>Your offer is ready!</H2>
            <Text>
              You’ll soon receive an email with a link to your offer. Please check your email. Note
              that it might take some time to arrive.
            </Text>
          </Box>
        </Box>
      ) : (
        <Box>
          <Box px={5} maxWidth="1200px" mx="auto">
            <Box py={5}>
              <Box textAlign="center">
                <Box display="inline-block" position="relative" mb={6}>
                  <CircularImage imgUrl={lastListingPhoto || DEFAULT_HOME_PHOTO} />
                  <Box
                    p={3}
                    width="40px"
                    height="40px"
                    bottom={0}
                    right={0}
                    position="absolute"
                    backgroundColor="neutrals0"
                    borderRadius="rounded"
                    boxShadow="0 5px 20px 0 rgba(0, 0, 0, 0.2)"
                  >
                    <ImgixImg width="24px" height="24px" src="green_check.svg" alt="" />
                  </Box>
                </Box>
                <H2>{renderHeadline()}</H2>
                <Box position="absolute" top="50%" left="50%">
                  <Confetti
                    active={blowConfetti}
                    config={{
                      angle: 90,
                      decay: 0.88,
                      elementCount: 140,
                      spread: 135,
                      startVelocity: 60,
                    }}
                  />
                </Box>
                <Box maxWidth={['90%', null, '720px']} mx="auto">
                  {renderSubText()}
                </Box>
              </Box>
            </Box>
            <FAQSection />
            <Box as="section" py={5}>
              <Flex
                flexDirection={['column', null, 'row']}
                justifyContent="space-around"
                alignItems="center"
              >
                <Box maxWidth={[null, null, '300px']} m={5} textAlign="center">
                  <ImgixImg
                    alt=""
                    src="hiw-step-7.png"
                    width="90px"
                    height="90px"
                    style={{ borderRadius: '50%' }}
                  />
                  <Text as="h6" mt={3}>
                    We’re always here to help
                  </Text>
                  {/* eslint-disable-next-line react/jsx-pascal-case */}
                  <Text fontSize="s0" lineHeight="s00" mt={3}>
                    Selling a home can be overwhelming. Feel free to reach out to us with any
                    questions at <a href={`mailto:${CXA_EMAIL_NEW}`}>{CXA_EMAIL_NEW}</a>.
                  </Text>
                </Box>
              </Flex>
            </Box>
          </Box>
        </Box>
      )}
    </ReceptionLoader>
  );
};

export default layoutWrapper(FlowComplete);
